import { useDispensing, useSearchableTabs } from '@hook'
import { DropDownIcon, TriDot } from '@icons'
import { DOCTOR_MODAL_TYPE, useModalStore } from '@modal'
import { Badge, DropdownBtn, Loader, Tab3, Table2, TitleHeader } from '@ui'
import { capitalizeDrugName, getError, getIncludedData, showToast } from '@util'
import { Box, Text } from 'grommet'
import { Checkmark } from 'grommet-icons'
import { useMemo, useState } from 'react'

export const tabOptions = [
  { key: '', label: 'All' },
  { key: 'dispensed', label: 'Dispensed' },
  { key: 'pending', label: 'Pending' }
]
const options = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Dispensed', value: 'dispensed' }
]
type PrescriptionType = 'inpatient' | 'outpatient'

interface PrescriptionFillModalProps {
  prescriptionType: PrescriptionType
}
const PrescriptionFill = ({ prescriptionType }: PrescriptionFillModalProps) => {
  const isOutpatient = prescriptionType === 'outpatient'
  const dispensaryType = isOutpatient ? 'outpatient_DAP' : 'inpatient_DAP'
  const { openModal } = useModalStore()
  const { activeTab, setActiveTab, searchParams } = useSearchableTabs(
    prescriptionType === 'inpatient' ? { tabKey: 'inpatient' } : {}
  )

  const [loading, setLoading] = useState(false)
  const search = searchParams.get('search') || ''
  const query = searchParams.get('query') || ''
  const page = searchParams.get('page') || ''
  const limit = searchParams.get('limit') || ''
  const { data, include, isLoading, updateDispense, pagination } = useDispensing({
    dispensary_type: dispensaryType,
    limit,
    page,
    query: isOutpatient ? search?.toLowerCase() : query?.toLowerCase(),
    status: prescriptionType === 'inpatient' ? activeTab || '' : activeTab === 'all' ? '' : activeTab
  })

  const handleSearch = (e: string) => {
    setActiveTab(e, 'query')
  }

  const handleUpdate = async (id: string) => {
    setLoading(true)
    const payload = {
      data: {
        attributes: {
          status: 'dispensed'
        },
        type: 'dispensing'
      }
    }
    try {
      await updateDispense({ id, payload })
      showToast('Marked as Dispensed')
    } catch (error) {
      getError(error)
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      id: 'patientId',
      header: 'Patient Id',
      cell: ({ row }: any) => {
        const { ref } = getIncludedData(include, 'patient', row.original.relationships?.patient?.data?.id).attributes
        return <Text size="14px">{ref}</Text>
      }
    },
    {
      id: 'patientName',
      header: 'Patient Name',
      cell: ({ row }: any) => {
        const { first_name, last_name } = getIncludedData(
          include,
          'patient',
          row.original.relationships?.patient?.data?.id
        ).attributes
        return (
          <Text className="capitalize" size="14px">
            {first_name} {last_name}
          </Text>
        )
      }
    },
    {
      id: 'medication',
      header: 'Medication',
      cell: ({ row }: any) => {
        const { drug_name } = getIncludedData(
          include,
          'product_inventory',
          row.original.relationships?.product_inventory?.data?.id
        ).attributes
        return <Text size="14px">{capitalizeDrugName(drug_name)}</Text>
      }
    },
    {
      id: 'quantity',
      header: 'Quantity Supplied',
      cell: ({ row }: any) => <Text size="14px">{row.original.attributes?.qty_dispensed}</Text>
    },
    {
      id: 'duration',
      header: 'Supply Duration',
      cell: ({ row }: any) => <Text size="14px">{row.original.attributes?.supply_duration}</Text>
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }: any) => (
        <Box direction="row" gap="xsmall">
          <Badge label={row.original.meta?.status} size="small" status={row.original.meta?.status} />
        </Box>
      )
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }: any) => {
        const {
          id,
          meta: { status }
        } = row.original
        return (
          <DropdownBtn
            dropdownLabel={<TriDot />}
            options={[
              {
                handleClick: (onClose: () => void) => {
                  openModal(DOCTOR_MODAL_TYPE.DISPENSE_DETAIL, { id })
                  onClose()
                },
                label: 'View details'
              },
              ...(status === 'pending'
                ? [
                    {
                      handleClick: (onClose: () => void) => {
                        openModal(DOCTOR_MODAL_TYPE.DESPENSE_AS_PRESCRIBE, {
                          dispenseId: id,
                          outpatient: true
                        })
                        onClose()
                      },
                      label: 'Edit Dose'
                    },
                    {
                      handleClick: (onClose: () => void) => {
                        handleUpdate(id)
                        onClose()
                      },
                      label: 'Mark as Dispensed'
                    }
                  ]
                : [])
            ]}
          />
        )
      }
    }
  ]

  const dropDown = useMemo(
    () => (
      <DropdownBtn
        dropdownLabel={
          <Box align="center" direction="row" gap="4px" justify="center">
            <Text size="small" style={{ textTransform: 'capitalize' }}>
              {tabOptions.find(t => t.key === activeTab)?.label}
            </Text>
            <DropDownIcon />
          </Box>
        }
        options={tabOptions?.map(t => ({
          activeBtn: activeTab === t.key,
          handleClick: (onClose: () => void) => {
            setActiveTab(t.key)
            onClose()
          },
          icon: activeTab === t.key ? <Checkmark size="small" /> : <></>,
          label: t.label
        }))}
        style={{
          border: `1px solid rgba(0, 0, 0, 0.33)`,
          borderRadius: '100px',
          minWidth: '123px',
          padding: '6px 12px'
        }}
      />
    ),
    [activeTab, tabOptions]
  )
  return (
    <>
      {isOutpatient ? (
        <div className="flex flex-col bg-white overflow-auto">
          {loading && <Loader />}
          <div className="flex flex-col gap-5">
            <TitleHeader title="Out-patients" />
            <Tab3 activeTab={activeTab || 'all'} setActiveTab={setActiveTab} tabs={options} />
          </div>
          <div className=" flex flex-col overflow-auto">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-end flex-wrap">
                <input
                  className="px-4 py-3 w-full sm:w-[300px] border border-gray-300 rounded-[25px] focus:outline-none focus:border-blue-500"
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="Search by name, medication, ID..."
                  type="text"
                  value={search}
                />
              </div>
              <div className="min-w-0 overflow-x-auto">
                <Table2
                  columns={columns}
                  data={data || []}
                  isLoading={isLoading}
                  message="No prescriptions found"
                  pageCount={pagination?.count || 0}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {loading && <Loader />}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between flex-wrap">
            <div className="flex flex-wrap gap-3">{dropDown}</div>
            <input
              className="px-4 py-3 w-full sm:w-[300px] border border-gray-300 rounded-[25px] focus:outline-none focus:border-blue-500"
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search by name, medication, ID..."
              type="text"
              value={query}
            />
          </div>
          <div className="min-w-0 overflow-x-auto">
            <Table2
              columns={columns}
              data={data || []}
              isLoading={isLoading}
              message="No prescriptions found"
              pageCount={pagination?.count || 0}
            />
          </div>
        </div>
      )}
    </>
  )
}

export default PrescriptionFill
